import { Route, NavigationGuardNext } from 'vue-router';
import store from '@/store';
import trackSeekerAccountCreation from '@/lib/tracking/segment_track_basic';
import { getSharedApolloClient } from '@/lib/apollo_setup';
import { fetchActiveScCareSessions } from '@/helpers/scCareSessionHelper';

const SESSION_STORAGE_KEY = 'corporate_onboarding_segment_events_fired';

function isCorporateOnboardingIndex(path: string): boolean {
  return path === '/corporate-onboarding' || path === '/corporate-onboarding/';
}

export function trackCorporateOnboardingSegment(): void {
  try {
    const account = store.getters['account/getAccount'];

    if (!account) {
      return;
    }

    const hasFiredEvents = sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';

    if (!hasFiredEvents) {
      const accountProperties = {
        firstName: account.firstName,
        lastName: account.lastName,
        email: account.email,
        familyType: account.accountType(),
      };

      trackSeekerAccountCreation(accountProperties);
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    }
  } catch (error) {
    console.error('Error in trackCorporateOnboardingSegment:', error);
  }
}

export async function resolveCorporateOnboardingFlow(to: Route, _from: Route, next: NavigationGuardNext): Promise<void> {
  const isIndexRoute = isCorporateOnboardingIndex(to.path);
  const isCareTypeRoute = to.path === '/corporate-onboarding/care-type';
  const isBackupCareRoute = to.path === '/corporate-onboarding/backup-care';

  if (!isIndexRoute && !isCareTypeRoute && !isBackupCareRoute) {
    next();
    return;
  }

  if (to.query.skip_backup_care === 'true') {
    if (isIndexRoute || isBackupCareRoute) {
      next({ path: '/corporate-onboarding/care-type', query: to.query, replace: true });
      return;
    }

    next();
    return;
  }

  try {
    const apolloClient = getSharedApolloClient();
    const activeSessions = await fetchActiveScCareSessions(apolloClient);
    const hasActiveBackupCare = activeSessions.length > 0;
    const targetPath = hasActiveBackupCare ? '/corporate-onboarding/backup-care' : '/corporate-onboarding/care-type';

    if (isIndexRoute) {
      next({ path: targetPath, query: to.query, replace: true });
      return;
    }

    if (hasActiveBackupCare && isCareTypeRoute) {
      next({ path: '/corporate-onboarding/backup-care', query: to.query, replace: true });
      return;
    }

    if (!hasActiveBackupCare && isBackupCareRoute) {
      next({ path: '/corporate-onboarding/care-type', query: to.query, replace: true });
      return;
    }

    next();
  } catch (error) {
    console.error('Error resolving corporate onboarding flow:', error);

    if (isIndexRoute || isBackupCareRoute) {
      next({
        path: '/corporate-onboarding/care-type',
        query: { ...to.query, skip_backup_care: 'true' },
        replace: true,
      });
      return;
    }

    next();
  }
}

const corporateOnboardingGuard = async (to: Route, from: Route, next: NavigationGuardNext): Promise<void> => {
  trackCorporateOnboardingSegment();
  await resolveCorporateOnboardingFlow(to, from, next);
};

export default corporateOnboardingGuard;
